import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import { useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { assertNotNil } from '../../../../utils/types';
import loadedSchemaToCopyDefinitionMenuItems from './loadedSchemaToCopyDefinitionMenuItems';
import { IDerivedColumnDraft } from '../../../../redux/slices/dataModeler';
import { useDataModelerPermittedSchemas } from '../utils';
import React, { useMemo } from 'react';
import DerivedColumnCopySchemaDefinitions from './DerivedColumnCopySchemaDefinitions';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import { MenuItemHeaderDiv } from './DataModelerDerivedColumnCopyDefinitionUI';
import ContextualMenuOption from '../../../../components/Dropdown/ContextualMenu/ContextualMenuOption';

interface IProps {
  updateDraft: (props: Partial<IDerivedColumnDraft>) => void;
}

const useDerivedColumnCopyDefinitionMenuItems = ({
  updateDraft,
}: IProps): IContextualMenuItem[] => {
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  const currentSchemaItems = useMemo(
    () =>
      loadedSchemaToCopyDefinitionMenuItems(schemaObj, draftEdits, updateDraft),
    [draftEdits, schemaObj, updateDraft],
  );

  const { permittedSchemas } = useDataModelerPermittedSchemas();

  return useMemo(() => {
    const result: IContextualMenuItem[] = [
      {
        key: 'header',
        onRender: () => <MenuItemHeaderDiv>Schema</MenuItemHeaderDiv>,
      },
      {
        key: 'header-divider',
        itemType: ContextualMenuItemType.Divider,
      },
      {
        key: `schema-${schemaObj.key}`,
        onRenderContent: () => (
          <ContextualMenuOption
            label={`${schemaObj.name} (current)`}
            hasSubMenus={true}
          />
        ),
        subMenuProps: {
          items: currentSchemaItems,
        },
      },
    ];
    permittedSchemas.forEach((schema) => {
      if (schema.id === schemaObj.key) {
        return;
      }
      result.push({
        key: schema.id,
        onRender: (_, dismissMenu) => {
          return (
            <DerivedColumnCopySchemaDefinitions
              forSchema={schema}
              updateDraft={updateDraft}
              dismissParent={dismissMenu}
            />
          );
        },
      });
    });
    return result;
  }, [
    currentSchemaItems,
    permittedSchemas,
    schemaObj.key,
    schemaObj.name,
    updateDraft,
  ]);
};

export default useDerivedColumnCopyDefinitionMenuItems;
